import { hexToRgba, getTextColor } from '../../../utils/colorUtils';

const Certificate = {
    formatFullDate: (date) => {
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    },

    // Add dynamic font sizing for course names
    getCourseFontSize: (courseName) => {
        const length = courseName.length;
        if (length <= 25) return 32;
        if (length <= 40) return 28;
        if (length <= 60) return 24;
        if (length <= 80) return 22;
        return 20;
    },

    // Calculate dynamic positioning for course name
    getCourseNameStyles: (courseName) => {
        const fontSize = Certificate.getCourseFontSize(courseName);
        const length = courseName.length;

        // Adjust container width and position based on text length
        let width = 451;
        let left = 251;

        if (length > 40) {
            width = 600;
            left = 176;
        }
        if (length > 60) {
            width = 700;
            left = 126;
        }

        return { fontSize, width, left };
    },

    imageUrlToDataUrl: async (url) => {
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error converting image to data URL:', error);
            return null;
        }
    },

    generateCertificate: async (course, user, tenancyData, tenancyBasics, primaryColor, lightColor, APP_URL) => {
        let userWithName = user;
        if (!user?.firstName || !user?.lastName) {
            const localStorageUser = JSON.parse(localStorage.getItem("user"));
            userWithName = localStorageUser || user;
        }

        try {
            const html2canvas = await import('html2canvas');

            let logoDataUrl = null;

            if (tenancyBasics?.logo) {
                const logoUrl = `${APP_URL}/${tenancyBasics.logo}`;
                logoDataUrl = await Certificate.imageUrlToDataUrl(logoUrl);
            }

            const primaryColorRgba = hexToRgba(primaryColor, 0.5);
            const whiteRgba = 'rgba(255, 255, 255, 0.5)';

            // Get contrast color for the rectangle background only
            const contrastTextColor = getTextColor(primaryColor);

            // Get dynamic styles for course name
            const courseStyles = Certificate.getCourseNameStyles(course.universityCourseName);

            const certificateElement = document.createElement('div');

            const logoHtml = logoDataUrl
                ? `<img src="${logoDataUrl}" alt="Company Logo" style="max-width: 550px; max-height: 175px; object-fit: contain;" />`
                : `<div style="font-family: Inter, sans-serif; color: #000000; font-weight: 700; font-size: 32px; letter-spacing: 2px;">${tenancyData?.name}</div>`;

            certificateElement.innerHTML = `
                <div style="
                    width: 1057px;
                    height: 816px;
                    background-color: #ffffff;
                    font-family: 'Inter', sans-serif;
                    position: relative;
                    margin: 0;
                    padding: 0;
                ">
                    <div style="
                        width: 953px;
                        height: 712px;
                        background: linear-gradient(135deg, ${primaryColorRgba} 0%, ${whiteRgba} 100%);
                        border-radius: 20px;
                        position: absolute;
                        top: 52px;
                        left: 52px;
                    ">
                <!-- Logo Section -->
             <div style="
                 position: absolute;
                 width: 600px;
                 height: 150px;
                 top: 60px; 
                 left: 180px;
                 display: flex;
                 align-items: center;
                 justify-content: center;
             ">
                 ${logoHtml}
             </div>
             
             <!-- Title Section -->
             <div style="
                 position: absolute;
                 width: 783px;
                 height: 64px;
                 top: 260px; 
                 left: 85px;
             ">
                 <!-- Background rectangle for "Certificate" -->
                 <div style="
                     position: absolute;
                     width: 326px;
                     height: 63px;
                     top: 1px;
                     left: 2px;
                     right: 30px;
                     background-color: ${primaryColor};
                 "></div>
                 
                 <!-- "Certificate" text (with dynamic color) -->
                 <div style="
                     position: absolute;
                     width: 326px;
                     top: 0;
                     left: 2px;
                     font-family: Inter, sans-serif;
                     font-weight: 600;
                     color: ${contrastTextColor};
                     font-size: 64px;
                     letter-spacing: 0;
                     line-height: normal;
                     white-space: nowrap;
                     text-align: center;
                     display: flex;
                     align-items: center;
                     justify-content: center;
                     height: 63px;
                 ">Certificate </div>
             
                 <!-- " of Completion" text (always black) -->
                 <div style="
                     position: absolute;
                     width: 455px;
                     top: 0;
                     left: 338px;
                     font-family: Inter, sans-serif;
                     font-weight: 600;
                     color: #000000;
                     font-size: 64px;
                     letter-spacing: 0;
                     line-height: normal;
                     white-space: nowrap;
                     display: flex;
                     align-items: center;
                     height: 63px;
                 "> of Completion</div>
             </div>
             
             <!-- "This is to certify that" Section -->
             <div style="
                 position: absolute;
                 width: 457px;
                 height: 41px;
                 top: 360px;
                 left: 254px;
             ">
                 <div style="
                     position: absolute;
                     top: 0;
                     left: 64px;
                     font-family: Inter, sans-serif;
                     font-weight: 500;
                     color: #000000;
                     font-size: 32px;
                     letter-spacing: 0;
                     line-height: normal;
                 ">This is to certify that</div>
             </div>
             
             <!-- Name Box -->
             <div style="
                 position: absolute;
                 width: 516px;
                 height: 61px;
                 top: 430px;
                 left: 218px;
                 background-color: #ffffff;
                 border-radius: 10px;
                 display: flex;
                 align-items: center;
                 justify-content: center;
                 font-family: Inter, sans-serif;
                 font-weight: 600;
                 color: #000000;
                 font-size: 28px;
             ">${userWithName?.firstName} ${userWithName?.lastName}</div>
             
             <!-- "Has successfully completed" -->
             <div style="
                 position: absolute;
                 top: 520px;
                 left: 342px;
                 font-family: Inter, sans-serif;
                 font-weight: 500;
                 color: #000000;
                 font-size: 20px;
                 letter-spacing: 0;
                 line-height: normal;
                 white-space: nowrap;
             ">Has successfully completed</div>
             
             <!-- Course Name with Dynamic Sizing -->
             <div style="
                 position: absolute;
                 top: 570px;
                 left: ${courseStyles.left}px;
                 width: ${courseStyles.width}px;
                 max-height: 80px;
                 font-family: Inter, sans-serif;
                 font-weight: 600;
                 color: #000000;
                 font-size: ${courseStyles.fontSize}px;
                 letter-spacing: 0;
                 line-height: 1.2;
                 text-align: center;
                 word-wrap: break-word;
                 overflow-wrap: break-word;
                 hyphens: auto;
                 display: flex;
                 align-items: flex-start;
                 justify-content: center;
             ">${course.universityCourseName}</div>
             
             <!-- Completion Date -->
             <div style="
                 position: absolute;
                 top: 640px;
                 left: 332px;
                 font-family: Inter, sans-serif;
                 font-weight: 500;
                 color: #000000;
                 font-size: 20px;
                 letter-spacing: 0;
                 line-height: normal;
                 white-space: nowrap;
             ">Completion date: ${Certificate.formatFullDate(new Date(course.dateCompleted))}</div>
                 </div>
             </div>
            `;

            // Load Inter font
            const fontLink = document.createElement('link');
            fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
            fontLink.rel = 'stylesheet';
            document.head.appendChild(fontLink);

            // Add to DOM temporarily (hidden but in viewport for better rendering)
            certificateElement.style.position = 'fixed';
            certificateElement.style.top = '-9999px';
            certificateElement.style.left = '-9999px';
            certificateElement.style.width = '1057px';
            certificateElement.style.height = '816px';
            certificateElement.style.backgroundColor = 'white';
            document.body.appendChild(certificateElement);

            // Wait for fonts and images to load
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Convert to canvas
            const canvas = await html2canvas.default(certificateElement, {
                width: 1057,
                height: 816,
                scale: 2,
                useCORS: false,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                onclone: (clonedDoc) => {
                    const style = clonedDoc.createElement('style');
                    style.textContent = `
                        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                        * { font-family: 'Inter', sans-serif !important; }
                    `;
                    clonedDoc.head.appendChild(style);
                }
            });

            // Convert canvas to blob
            canvas.toBlob((blob) => {
                // Create download link
                const link = document.createElement('a');
                link.download = `certificate-${userWithName?.firstName}-${userWithName?.lastName}-${course.universityCourseName?.replace(/[^a-zA-Z0-9]/g, '-')}.png`;
                link.href = URL.createObjectURL(blob);

                // Trigger download
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                // Clean up
                URL.revokeObjectURL(link.href);
            }, 'image/png', 1.0);

            // Remove temporary elements
            document.body.removeChild(certificateElement);
            document.head.removeChild(fontLink);

        } catch (error) {
            console.error('Error generating certificate:', error);
            alert('Error generating certificate. Please try again.');
        }
    }
};

export default Certificate;